import { useState, useCallback } from "react";
import { apiClient } from "../lib/api";
import { setToken, setUser, getUser, clearToken, getToken, getDeviceToken, setDeviceToken, setLoginFlags } from "../lib/auth";

// Acceso en pasos: contraseña → (si los 2 pasos están activos y el dispositivo no es de confianza) código de Google Authenticator.
// Si la app falla: código de recuperación, o código por WhatsApp/correo + PIN. Contraseña olvidada: PIN + código y una nueva.
export function useAuth() {
  const [user, setUserState] = useState(getUser());

  // Cuando el servidor da la sesión («ok»): se guarda el token, el usuario, el dispositivo de confianza y los avisos de después de entrar.
  const finish = useCallback((data) => {
    setToken(data.access_token);
    setUser(data.user);
    setUserState(data.user);
    if (data.device_token) setDeviceToken(data.device_token);
    setLoginFlags({
      needs_totp_setup: !!data.needs_totp_setup,
      must_setup_totp: !!data.must_setup_totp,
      must_change_password: !!data.must_change_password,
      password_days_left: data.password_days_left ?? null,
    });
    return data;
  }, []);

  const post = useCallback(async (url, body) => (await apiClient.post(url, body)).data, []);

  // → {status: "ok", ...} (ya con sesión) o {status: "second_factor", challenge, methods}
  const login = useCallback(
    async (email, password) => {
      const data = await post("/login", { email, password, device_token: getDeviceToken() });
      return data.status === "ok" ? finish(data) : data;
    },
    [post, finish]
  );
  const loginTotp = useCallback(async (challenge, code, remember) => finish(await post("/login/totp", { challenge, code, remember: !!remember })), [post, finish]);
  const loginRecoveryCode = useCallback(async (challenge, code) => finish(await post("/login/recovery-code", { challenge, code })), [post, finish]);
  const fallbackRequest = useCallback((challenge, channel) => post("/login/fallback/request", { challenge, channel }), [post]);
  const fallbackVerify = useCallback(async (challenge, channel, code, pin) => finish(await post("/login/fallback/verify", { challenge, channel, code, pin })), [post, finish]);
  const forgotRequest = useCallback((email, channel) => post("/login/forgot/request", { email, channel }), [post]);
  const forgotConfirm = useCallback((email, channel, code, pin, newPassword) => post("/login/forgot/confirm", { email, channel, code, pin, new_password: newPassword }), [post]);

  const logout = useCallback(() => {
    const token = getToken();
    // cierra la sesión en el servidor (con el token ya capturado: aquí mismo se borra); si falla, igual se sale de aquí
    if (token) apiClient.post("/logout", null, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearToken();
    setUserState(null);
  }, []);

  return { user, isAuthenticated: !!getToken(), login, loginTotp, loginRecoveryCode, fallbackRequest, fallbackVerify, forgotRequest, forgotConfirm, logout };
}
